import { EMAIL_NOT_VERIFIED } from '@/lib/email-verification';

export interface ScopeInfo {
  value: string;
  label: string;
  description: string;
  mutation: boolean;
  paidResources: boolean;
  requiresPaidPlan: boolean;
}

// Mirrors backend PersonalAccessTokenScope; descriptions follow the endpoints each scope unlocks.
export const PAT_SCOPES: ScopeInfo[] = [
  {
    value: 'profile:read',
    label: 'Read profile',
    description: 'Read your username and name, and details of the token in use.',
    mutation: false,
    paidResources: false,
    requiresPaidPlan: false,
  },
  {
    value: 'runners:read',
    label: 'Read runners',
    description: 'List runners and read their details and metrics.',
    mutation: false,
    paidResources: false,
    requiresPaidPlan: false,
  },
  {
    value: 'runners:write',
    label: 'Manage runners',
    description:
      'Create, update, pause, resume and duplicate runners. Creating, resuming and duplicating runners creates Hetzner servers that you pay for.',
    mutation: true,
    paidResources: true,
    requiresPaidPlan: false,
  },
  {
    value: 'runners:delete',
    label: 'Delete runners',
    description: 'Delete runners together with their Hetzner servers.',
    mutation: true,
    paidResources: false,
    requiresPaidPlan: false,
  },
  {
    value: 'runners:forget',
    label: 'Remove unknown runners',
    description:
      'Remove runners in an unknown state from the dashboard without deleting their Hetzner servers.',
    mutation: true,
    paidResources: false,
    requiresPaidPlan: false,
  },
  {
    value: 'schedules:read',
    label: 'Read schedules',
    description: 'Read runner pause and resume schedules.',
    mutation: false,
    paidResources: false,
    requiresPaidPlan: true,
  },
  {
    value: 'schedules:write',
    label: 'Manage schedules',
    description: 'Create, change and delete runner pause and resume schedules.',
    mutation: true,
    paidResources: false,
    requiresPaidPlan: true,
  },
  {
    value: 'products:read',
    label: 'Read products',
    description: 'List available runner server types, locations and prices.',
    mutation: false,
    paidResources: false,
    requiresPaidPlan: false,
  },
  {
    value: 'providers:ssh-keys:read',
    label: 'Read SSH keys',
    description: 'List the SSH keys in your Hetzner project.',
    mutation: false,
    paidResources: false,
    requiresPaidPlan: false,
  },
  {
    value: 'subscriptions:read',
    label: 'Read subscription',
    description: 'Read your subscription plan and billing status.',
    mutation: false,
    paidResources: false,
    requiresPaidPlan: false,
  },
];

// The same read-only set the CLI requests by default on `auth login`.
export const READ_ONLY_SCOPES = PAT_SCOPES.filter((s) => !s.mutation).map((s) => s.value);

export const PAT_NAME_MAX_LENGTH = 96;

const DAY_MS = 24 * 60 * 60 * 1000;
// The backend rejects expiries more than 365 days ahead of its own clock; the margin absorbs
// request latency and small client clock drift.
const MAX_TTL_MS = 365 * DAY_MS - 10 * 60 * 1000;

export interface ExpiryOption {
  id: string;
  label: string;
  ttlMs: number;
}

export const EXPIRY_OPTIONS: ExpiryOption[] = [
  { id: '7d', label: '7 days', ttlMs: 7 * DAY_MS },
  { id: '30d', label: '30 days', ttlMs: 30 * DAY_MS },
  { id: '90d', label: '90 days', ttlMs: 90 * DAY_MS },
  { id: '1y', label: '1 year (maximum)', ttlMs: MAX_TTL_MS },
];

export const DEFAULT_EXPIRY_OPTION = '30d';

export function expiryFromOption(optionId: string, now: Date = new Date()): Date {
  const option = EXPIRY_OPTIONS.find((o) => o.id === optionId) ?? EXPIRY_OPTIONS[1]!;
  return new Date(now.getTime() + option.ttlMs);
}

export function scopeInfo(value: string): ScopeInfo | undefined {
  return PAT_SCOPES.find((s) => s.value === value);
}

export type TokenStatus = 'active' | 'expired' | 'revoked';

export function tokenStatus(
  token: { revokedAt: string | null; expiresAt: string },
  now: Date = new Date()
): TokenStatus {
  if (token.revokedAt) return 'revoked';
  return Date.parse(token.expiresAt) <= now.getTime() ? 'expired' : 'active';
}

export function displayPrefix(selector: string): string {
  return `mr_pat_${selector}_…`;
}

export function validateTokenName(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return 'Enter a name.';
  if (trimmed.length > PAT_NAME_MAX_LENGTH) {
    return `Use at most ${PAT_NAME_MAX_LENGTH} characters.`;
  }
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f-\u009f]/.test(trimmed)) return 'Remove control characters.';
  return null;
}

const VALIDATION_MESSAGES: Record<string, string> = {
  'name:blank': 'Enter a name.',
  'name:too_long': `Use at most ${PAT_NAME_MAX_LENGTH} characters for the name.`,
  'name:invalid_characters': 'Remove control characters from the name.',
  'scopes:empty': 'Select at least one scope.',
  'scopes:unknown': 'One of the selected scopes is not supported. Reload the page and try again.',
  'expiresAt:not_after_now': 'The expiry must be in the future. Check your device clock.',
  'expiresAt:exceeds_maximum':
    'Tokens can be valid for at most one year. Choose a shorter expiry or check your device clock.',
};

export interface TokenApiError {
  message: string;
  field?: 'name' | 'scopes' | 'expiresAt';
  status?: number;
  emailNotVerified?: boolean;
}

interface ProblemBody {
  code?: string;
  field?: string;
  reason?: string;
  detail?: string;
  requestId?: string;
}

export function tokenApiError(e: unknown, action: string): TokenApiError {
  const { response } = (e ?? {}) as { response?: { status?: number; data?: unknown } };
  if (!response) {
    return {
      message: `Could not ${action}: the server is unreachable. Check your connection and try again.`,
    };
  }
  const status = response.status;
  const body = (
    response.data && typeof response.data === 'object' ? response.data : {}
  ) as ProblemBody;
  const reference = body.requestId ? ` (request ID ${body.requestId})` : '';

  if (status === 400 && body.code === 'INVALID_PERSONAL_ACCESS_TOKEN_REQUEST') {
    const field = body.field as TokenApiError['field'];
    const known = VALIDATION_MESSAGES[`${body.field}:${body.reason}`];
    const message = known
      ? `${known}${body.requestId ? ` Request ID: ${body.requestId}.` : ''}`
      : `The request was rejected${reference}.`;
    return { message, field, status };
  }
  if (status === 403 && body.code === EMAIL_NOT_VERIFIED) {
    return {
      message:
        'Verify your email address before creating access tokens. Enter the 6-digit code we emailed you, or request a new one.',
      status,
      emailNotVerified: true,
    };
  }
  if (status === 401) {
    return { message: 'Your session has expired. Sign in again.', status };
  }
  if (status === 403) {
    return {
      message:
        'You are not allowed to manage access tokens with this session. Sign in again, completing multi-factor authentication if enabled.',
      status,
    };
  }
  if (status === 404) {
    return { message: `The token no longer exists${reference}.`, status };
  }
  return {
    message: `Could not ${action} (HTTP ${status ?? 'error'})${reference}. Try again later.`,
    status,
  };
}
